import React, { useState } from 'react';
import axios from 'axios';

// Create optimized API client with timeouts and retry logic
const createApiClient = (baseURL, timeout = 10000) => {
  const client = axios.create({
    baseURL,
    timeout,
    retry: 3,
    retryDelay: 1000,
    retryCondition: (error) => {
      // Retry on network errors or 5xx status codes
      return !error.response || (error.response.status >= 500 && error.response.status < 600);
    }
  });

  // Add authentication interceptor
  client.interceptors.request.use(
    config => {
      // Get sessionId from localStorage (matching useAuth.js)
      const sessionId = localStorage.getItem('sessionId');
      if (sessionId) {
        config.headers.Authorization = `Bearer ${sessionId}`;
      }
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  // Add retry interceptor
  client.interceptors.response.use(
    response => response,
    async error => {
      // Handle 401 Unauthorized - redirect to login
      if (error.response?.status === 401) {
        console.warn('🔒 Authentication failed - redirecting to login');
        localStorage.removeItem('sessionId');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      const config = error.config;
      
      if (!config || !config.retry) {
        return Promise.reject(error);
      }
      
      config.retryCount = config.retryCount || 0;
      
      if (config.retryCount >= config.retry) {
        return Promise.reject(error);
      }
      
      config.retryCount++;
      
      // Exponential backoff
      const delay = config.retryDelay * Math.pow(2, config.retryCount - 1);
      await new Promise(resolve => setTimeout(resolve, delay));
      
      console.log(`🔄 Retrying request (${config.retryCount}/${config.retry}): ${config.url}`);
      
      return client(config);
    }
  );

  // Add request interceptor for logging
  client.interceptors.request.use(
    config => {
      console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
      return config;
    },
    error => {
      console.error('❌ Request error:', error);
      return Promise.reject(error);
    }
  );

  // Add response interceptor for performance monitoring
  client.interceptors.response.use(
    response => {
      const duration = Date.now() - response.config.metadata?.startTime;
      if (duration > 5000) {
        console.warn(`🐌 Slow API response: ${response.config.url} took ${duration}ms`);
      }
      return response;
    },
    error => {
      const duration = Date.now() - error.config?.metadata?.startTime;
      console.error(`❌ API Error: ${error.config?.url} failed after ${duration}ms`);
      return Promise.reject(error);
    }
  );

  return client;
};

// Create API clients for different endpoints
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

export const apiClient = createApiClient(API_URL, 15000); // 15 second timeout for general API
export const dashboardClient = createApiClient(`${API_URL}/dashboard`, 20000); // 20 second timeout for dashboard
export const campaignClient = createApiClient(`${API_URL}/campaign`, 25000); // 25 second timeout for campaigns
export const filteredCustomersClient = createApiClient(`${API_URL}/dashboard`, 60000); // 60 second timeout for filtered customers

// Progressive loading hook
export const useProgressiveLoading = () => {
  const [loadingStates, setLoadingStates] = useState({
    critical: false,
    secondary: false,
    complete: false
  });

  const loadCriticalData = async (loadFn) => {
    setLoadingStates(prev => ({ ...prev, critical: true }));
    try {
      const result = await loadFn();
      setLoadingStates(prev => ({ ...prev, critical: false }));
      return result;
    } catch (error) {
      setLoadingStates(prev => ({ ...prev, critical: false }));
      throw error;
    }
  };

  const loadSecondaryData = async (loadFn) => {
    setLoadingStates(prev => ({ ...prev, secondary: true }));
    try {
      const result = await loadFn();
      setLoadingStates(prev => ({ ...prev, secondary: false, complete: true }));
      return result;
    } catch (error) {
      setLoadingStates(prev => ({ ...prev, secondary: false }));
      throw error;
    }
  };

  return {
    loadingStates,
    loadCriticalData,
    loadSecondaryData
  };
};

// Pagination hook for large datasets
export const usePagination = (initialPage = 1, initialLimit = 50) => {
  const [pagination, setPagination] = useState({
    page: initialPage,
    limit: initialLimit,
    hasMore: true,
    totalPages: 0,
    totalItems: 0
  });

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadPage = async (loadFn, page = pagination.page) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await loadFn(page, pagination.limit);
      
      if (page === 1) {
        setData(result.data || result);
      } else {
        setData(prev => [...prev, ...(result.data || result)]);
      }
      
      setPagination(prev => ({
        ...prev,
        page,
        hasMore: result.pagination?.hasMore || false,
        totalPages: result.pagination?.totalPages || 0,
        totalItems: result.pagination?.totalItems || result.count || 0
      }));
      
    } catch (err) {
      setError(err.message);
      console.error('Pagination error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadNextPage = () => {
    if (pagination.hasMore && !loading) {
      loadPage(undefined, pagination.page + 1);
    }
  };

  const resetPagination = () => {
    setPagination({
      page: initialPage,
      limit: initialLimit,
      hasMore: true,
      totalPages: 0,
      totalItems: 0
    });
    setData([]);
    setError(null);
  };

  return {
    data,
    pagination,
    loading,
    error,
    loadPage,
    loadNextPage,
    resetPagination
  };
};

// Performance monitoring hook
export const usePerformanceMonitor = () => {
  const [metrics, setMetrics] = useState({
    responseTime: 0,
    memoryUsage: 0,
    errorCount: 0,
    successCount: 0
  });

  const trackRequest = (startTime, success = true) => {
    const responseTime = Date.now() - startTime;
    
    setMetrics(prev => ({
      ...prev,
      responseTime: responseTime,
      successCount: success ? prev.successCount + 1 : prev.successCount,
      errorCount: success ? prev.errorCount : prev.errorCount + 1
    }));

    // Log slow requests
    if (responseTime > 5000) {
      console.warn(`🐌 Slow request detected: ${responseTime}ms`);
    }
  };

  const getMemoryUsage = () => {
    if (performance.memory) {
      return {
        used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024),
        total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024),
        limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024)
      };
    }
    return null;
  };

  return {
    metrics,
    trackRequest,
    getMemoryUsage
  };
};

// Circuit breaker hook for frontend
export const useCircuitBreaker = (threshold = 5, timeout = 60000) => {
  const [state, setState] = useState('CLOSED');
  const [failureCount, setFailureCount] = useState(0);
  const [lastFailureTime, setLastFailureTime] = useState(null);

  const execute = async (operation) => {
    if (state === 'OPEN') {
      if (Date.now() - lastFailureTime < timeout) {
        throw new Error('Circuit breaker is OPEN');
      } else {
        setState('HALF_OPEN');
      }
    }

    try {
      const result = await operation();
      setFailureCount(0);
      setState('CLOSED');
      return result;
    } catch (error) {
      const newFailureCount = failureCount + 1;
      setFailureCount(newFailureCount);
      setLastFailureTime(Date.now());
      
      if (newFailureCount >= threshold) {
        setState('OPEN');
      }
      
      throw error;
    }
  };

  return {
    state,
    failureCount,
    execute
  };
};

export default {
  apiClient,
  dashboardClient,
  campaignClient,
  useProgressiveLoading,
  usePagination,
  usePerformanceMonitor,
  useCircuitBreaker
};
